/**
 * 전송 계층 — 콘솔이 호스트에 따라 다르게 통신하는 지점.
 *
 * 콘솔 UI 코드는 이 인터페이스만 안다. 그래서 **같은 화면 코드가** 독립 웹으로도,
 * 다른 서비스 안 iframe 으로도, (호스트가 네트워크 capability 를 갖추면) 격리
 * 플러그인 번들로도 돌아간다. 통합 경로를 바꾸는 데 UI 를 고치지 않는다.
 *
 * PLAN §5 의 3경로가 여기 세 구현으로 대응한다.
 */

export interface TransportResponse<T> {
  ok: boolean;
  status: number;
  data?: T;
  error?: string;
  details?: unknown;
}

export interface Transport {
  readonly kind: 'direct' | 'embed' | 'host-bridge';
  request<T>(method: string, path: string, body?: unknown): Promise<TransportResponse<T>>;
  /** 미리보기 이미지처럼 브라우저가 직접 로드해야 하는 URL 을 만든다. */
  mediaUrl(path: string): string;
}

const REQUEST_TIMEOUT_MS = 20_000;

/** iframe 임베드에서 호스트가 새 토큰을 줄 때까지 기다리는 한도. */
const TOKEN_WAIT_MS = 10_000;

async function parseResponse<T>(response: Response): Promise<TransportResponse<T>> {
  if (response.status === 204) return { ok: true, status: 204 };

  const text = await response.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    const body = parsed as { message?: string; details?: unknown } | null;
    return {
      ok: false,
      status: response.status,
      error: body?.message ?? `HTTP ${response.status}`,
      details: body?.details,
    };
  }
  return { ok: true, status: response.status, data: parsed as T };
}

/**
 * 경로 A(독립 웹) · 기본 경로.
 *
 * 토큰은 호스트가 넘겨준 신원 JWT 이거나 서비스 토큰이다. 콘솔은 토큰을 **저장하지
 * 않는다** — 메모리에만 둔다. 호스트 신원 토큰은 수명이 짧아 어차피 저장할 값이 아니고,
 * 저장하면 그것이 곧 탈취 표면이 된다.
 */
export class DirectTransport implements Transport {
  readonly kind = 'direct' as const;
  private token: string | null;

  constructor(
    private readonly baseUrl: string,
    token: string | null = null,
  ) {
    this.token = token;
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  mediaUrl(path: string): string {
    return /^https?:\/\//i.test(path) ? path : this.baseUrl.replace(/\/+$/, '') + path;
  }

  async request<T>(method: string, path: string, body?: unknown): Promise<TransportResponse<T>> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const headers: Record<string, string> = {};
      if (body !== undefined) headers['content-type'] = 'application/json';
      if (this.token) headers.authorization = `Bearer ${this.token}`;

      const response = await fetch(this.mediaUrl(path), {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
        /*
         * 로컬 계정 세션 쿠키를 싣는다. 쿠키는 httpOnly 라 자바스크립트가 읽을 수
         * 없고, 여기서 보내지 않으면 아예 붙지 않는다.
         *
         * 콘솔이 서버와 다른 오리진이면 서버 쪽에 `CORS_ORIGINS` 등록과
         * `SESSION_COOKIE_SAMESITE=none` + Secure 가 필요하다.
         */
        credentials: 'include',
      });
      return await parseResponse<T>(response);
    } catch (error) {
      return {
        ok: false,
        status: 0,
        error: error instanceof Error ? error.message : '네트워크 오류',
      };
    } finally {
      clearTimeout(timer);
    }
  }
}

/**
 * 경로 A' — iframe 임베드.
 *
 * 호스트가 **자기 백엔드에서** 단명 임베드 토큰을 만들어 postMessage 로 넘긴다.
 * 콘솔은 그 토큰으로 SignageOne 을 직접 호출한다. 호스트 런타임 변경이 필요 없어
 * 오늘 당장 어떤 호스트에도 붙는다(PLAN §5 경로 A).
 *
 * 토큰이 만료되면 호스트에 갱신을 요청한다 — 사용자가 다시 로그인할 일이 없다.
 */
export class EmbedTransport implements Transport {
  readonly kind = 'embed' as const;
  private readonly direct: DirectTransport;
  private pendingToken: Promise<string | null> | null = null;

  constructor(
    baseUrl: string,
    private readonly allowedOrigin: string,
  ) {
    this.direct = new DirectTransport(baseUrl);
    window.addEventListener('message', (event) => this.onHostMessage(event));
  }

  private onHostMessage(event: MessageEvent): void {
    // 오리진을 반드시 검사한다. 아무 페이지나 토큰을 주입할 수 있으면 안 된다.
    if (event.origin !== this.allowedOrigin) return;
    const data = event.data as { type?: string; token?: string } | null;
    if (data?.type === 'signageone:token' && typeof data.token === 'string') {
      this.direct.setToken(data.token);
    }
  }

  /**
   * 호스트에 토큰을 요청한다. 응답은 message 이벤트로 온다.
   *
   * **끝낼 때는 제 요청만 치운다.** 예전에는 응답이 와도 10초 타이머가 그대로 남아, 그 사이 새로
   * 시작한 요청을 지워 버렸다 — 그다음 401 은 이미 날아간 요청을 모르고 호스트에 또 묻는다.
   * 응답이 오면 타이머를 걷고, 대기 자리는 그것이 여전히 내 것일 때만 비운다.
   */
  private requestToken(): Promise<string | null> {
    if (this.pendingToken) return this.pendingToken;
    const pending: Promise<string | null> = new Promise<string | null>((resolve) => {
      const finish = (token: string | null): void => {
        clearTimeout(timer);
        window.removeEventListener('message', onMessage);
        if (this.pendingToken === pending) this.pendingToken = null;
        resolve(token);
      };
      const onMessage = (event: MessageEvent): void => {
        if (event.origin !== this.allowedOrigin) return;
        const data = event.data as { type?: string; token?: string } | null;
        if (data?.type === 'signageone:token') finish(typeof data.token === 'string' ? data.token : null);
      };
      window.addEventListener('message', onMessage);
      // 호스트가 응답하지 않으면 무한히 기다리지 않는다.
      const timer = setTimeout(() => finish(null), TOKEN_WAIT_MS);
      window.parent.postMessage({ type: 'signageone:request-token' }, this.allowedOrigin);
    });
    this.pendingToken = pending;
    return pending;
  }

  mediaUrl(path: string): string {
    return this.direct.mediaUrl(path);
  }

  async request<T>(method: string, path: string, body?: unknown): Promise<TransportResponse<T>> {
    const first = await this.direct.request<T>(method, path, body);
    if (first.status !== 401) return first;

    // 토큰이 만료됐다 — 호스트에 새로 받아 한 번만 재시도한다.
    const token = await this.requestToken();
    if (!token) return first;
    this.direct.setToken(token);
    return this.direct.request<T>(method, path, body);
  }
}

/**
 * 경로 C — 호스트 격리 런타임(TalkOne v2 플러그인 번들).
 *
 * 격리 웹뷰는 CSP 로 외부 네트워크가 막혀 있어 `fetch` 를 직접 쓸 수 없다. 대신
 * 호스트가 노출한 capability 로 요청을 위임하고, 호스트 메인 프로세스가 게이트웨이를
 * 거쳐 SignageOne 을 호출한다.
 *
 * **현재 TalkOne 에는 이 capability 가 없다**(PLAN §1.3 G1). 그래서 이 구현은 호스트가
 * `integrations/talkone-host-patch` 의 사양을 채택하면 켜지는 경로이고, 그때까지는
 * `isAvailable()` 이 false 를 돌려준다. 콘솔은 그 사실을 알고 다른 경로로 뜬다.
 */
interface HostBridge {
  net?: {
    fetch(input: {
      method: string;
      path: string;
      body?: string;
      headers?: Record<string, string>;
    }): Promise<{ status: number; body: string }>;
  };
}

export class HostBridgeTransport implements Transport {
  readonly kind = 'host-bridge' as const;

  static isAvailable(): boolean {
    const bridge = (globalThis as { talkone?: HostBridge }).talkone;
    return typeof bridge?.net?.fetch === 'function';
  }

  private get bridge(): NonNullable<HostBridge['net']> {
    const bridge = (globalThis as { talkone?: HostBridge }).talkone?.net;
    if (!bridge) throw new Error('호스트 네트워크 capability 가 없습니다');
    return bridge;
  }

  /**
   * 미디어는 호스트가 예약 경로로 프록시해야 한다(PLAN §1.3 G2).
   * 그 경로도 없으면 미리보기가 뜨지 않는다 — 콘솔은 그 상태에서도 목록·편성은 동작한다.
   */
  mediaUrl(path: string): string {
    return `/_host/media${path}`;
  }

  async request<T>(method: string, path: string, body?: unknown): Promise<TransportResponse<T>> {
    try {
      const response = await this.bridge.fetch({
        method,
        path,
        body: body === undefined ? undefined : JSON.stringify(body),
        headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      });

      let parsed: unknown = null;
      try {
        parsed = response.body ? JSON.parse(response.body) : null;
      } catch {
        parsed = null;
      }

      if (response.status >= 400) {
        const errorBody = parsed as { message?: string; details?: unknown } | null;
        return {
          ok: false,
          status: response.status,
          error: errorBody?.message ?? `HTTP ${response.status}`,
          details: errorBody?.details,
        };
      }
      return { ok: true, status: response.status, data: parsed as T };
    } catch (error) {
      return {
        ok: false,
        status: 0,
        error: error instanceof Error ? error.message : '호스트 브리지 오류',
      };
    }
  }
}

export interface TransportConfig {
  baseUrl: string;
  token?: string | null;
  /** iframe 임베드일 때 신뢰할 부모 오리진. */
  parentOrigin?: string;
}

/** 전송 방식 선택에 필요한 실행 환경 사실. 주입 가능하게 두어 순수 함수로 검증한다. */
export interface TransportEnvironment {
  /** 호스트가 네트워크 capability 를 노출했는가. */
  hasHostBridge: boolean;
  /** iframe 안에서 돌고 있는가. */
  isEmbedded: boolean;
}

/**
 * 실행 환경을 보고 전송 방식을 고른다.
 *
 * 우선순위: 호스트 브리지(가장 강한 격리) → iframe 임베드 → 직접 호출.
 * 콘솔을 띄우는 쪽이 무엇을 줬는지에 따라 자동으로 정해지므로, **배포 산출물은 하나**다.
 *
 * 순수 함수로 분리한 이유: 이 판단이 틀리면 콘솔이 통째로 뜨지 않는데, DOM 을 띄우지
 * 않고는 검증할 수 없는 형태로 두면 그 사실을 배포 후에야 알게 된다.
 */
export function selectTransportKind(
  config: TransportConfig,
  environment: TransportEnvironment,
): Transport['kind'] {
  if (environment.hasHostBridge) return 'host-bridge';
  if (config.parentOrigin && environment.isEmbedded) return 'embed';
  return 'direct';
}

/** 현재 실행 환경을 관찰한다. */
export function detectEnvironment(): TransportEnvironment {
  return {
    hasHostBridge: HostBridgeTransport.isAvailable(),
    isEmbedded: typeof window !== 'undefined' && window.parent !== window,
  };
}

export function createTransport(
  config: TransportConfig,
  environment: TransportEnvironment = detectEnvironment(),
): Transport {
  switch (selectTransportKind(config, environment)) {
    case 'host-bridge':
      return new HostBridgeTransport();
    case 'embed':
      return new EmbedTransport(config.baseUrl, config.parentOrigin as string);
    default:
      return new DirectTransport(config.baseUrl, config.token ?? null);
  }
}
